/**
 * EntityBadge — Composant unifié d'affichage d'une affiliation (V9.13 Sprint 2 UX).
 *
 * Représente une entité liée (club, coach, fédération, athlète, sponsor…) avec :
 *   - avatar/logo (photo_url ou logo_url), fallback initiales
 *   - nom
 *   - lien vers la fiche si `slug` est disponible
 *   - **coche verte** de validation **UNIQUEMENT** si `link_status === "accepted"`
 *     (règle stricte : signifie que la relation d'affiliation est validée).
 *
 * ⚠️ NE mélange PAS avec la notion de "profil vérifié par son propriétaire" —
 * ce sera un autre badge dédié plus tard.
 *
 * Props :
 *   - kind         : "club" | "coach" | "federation" | "athlete" | "sponsor" (facultatif)
 *   - name         : string (obligatoire)
 *   - slug         : string | null
 *   - image        : string | null
 *   - link_status  : "accepted" | "pending" | "declared" | "rejected" | null
 *   - size         : "sm" (default) | "md" | "lg"
 *   - variant      : "row" (default) | "chip"
 *   - hideStatusText : boolean — masque l'étiquette texte de statut (chip only)
 *   - showPendingHint : bool — affiche une petite icône horloge si pending/declared
 */
import { Link } from "react-router-dom";
import { CheckCircle2, Clock, XCircle, User, Building2, Crown, Award } from "lucide-react";

const KIND_PATH = {
  club: "/clubs/",
  coach: "/coachs/",
  federation: "/federations/",
  athlete: "/athletes/",
  sponsor: "/sponsors/",
};

const KIND_ICON = {
  club: Building2,
  coach: User,
  federation: Crown,
  athlete: User,
  sponsor: Award,
};

const SIZE = {
  sm: { avatar: "h-7 w-7", text: "text-sm", gap: "gap-2", check: "h-3.5 w-3.5" },
  md: { avatar: "h-9 w-9", text: "text-sm", gap: "gap-2.5", check: "h-4 w-4" },
  lg: { avatar: "h-12 w-12", text: "text-base", gap: "gap-3", check: "h-5 w-5" },
};

function initials(name = "") {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function EntityBadge({
  kind = "club",
  name,
  slug,
  image,
  link_status = null,
  size = "sm",
  variant = "row",
  hideStatusText = false,
  showPendingHint = true,
  className = "",
}) {
  if (!name) return null;
  const s = SIZE[size] || SIZE.sm;
  const IconFallback = KIND_ICON[kind] || Building2;
  const isAccepted = link_status === "accepted";
  const isPending = link_status === "pending" || link_status === "declared";
  const isRejected = link_status === "rejected";
  const targetPath = slug ? `${KIND_PATH[kind] || "/"}${slug}` : null;

  const inner = (
    <span className={`inline-flex items-center ${s.gap} min-w-0`}>
      {/* Avatar */}
      {image ? (
        <img
          src={image}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className={`${s.avatar} rounded-md object-cover shrink-0 border border-border`}
        />
      ) : (
        <span
          className={`${s.avatar} rounded-md shrink-0 border border-border bg-muted flex items-center justify-center text-[10px] font-medium text-muted-foreground uppercase`}
          aria-hidden="true"
        >
          {initials(name)}
        </span>
      )}

      {/* Nom + coche */}
      <span className={`${s.text} min-w-0 flex items-center gap-1 truncate font-medium`}>
        <span className="truncate" title={name}>{name}</span>
        {isAccepted && (
          <CheckCircle2
            className={`${s.check} shrink-0 text-emerald-600`}
            aria-label="Affiliation validée"
            data-testid={`entity-badge-check-${kind}`}
          />
        )}
        {!isAccepted && isPending && showPendingHint && (
          <Clock
            className={`${s.check} shrink-0 text-amber-500`}
            aria-label="Affiliation en attente"
            data-testid={`entity-badge-pending-${kind}`}
          />
        )}
        {isRejected && showPendingHint && (
          <XCircle
            className={`${s.check} shrink-0 text-muted-foreground`}
            aria-label="Affiliation rejetée"
            data-testid={`entity-badge-rejected-${kind}`}
          />
        )}
      </span>
    </span>
  );

  if (variant === "chip") {
    const chipClass = `inline-flex items-center rounded-full border pl-1 pr-3 py-0.5 bg-background hover:bg-muted transition ${className}`;
    return targetPath ? (
      <Link to={targetPath} className={chipClass} data-testid={`entity-badge-${kind}-${slug || name}`}>
        {inner}
        {!hideStatusText && isAccepted && (
          <span className="ml-1.5 text-[10px] text-emerald-700">validée</span>
        )}
      </Link>
    ) : (
      <span className={chipClass} data-testid={`entity-badge-${kind}-${name}`}>{inner}</span>
    );
  }

  // variant "row" (default)
  const rowClass = `flex items-center min-w-0 ${targetPath ? "hover:underline" : ""} ${className}`;
  return targetPath ? (
    <Link to={targetPath} className={rowClass} data-testid={`entity-badge-${kind}-${slug || name}`}>
      {inner}
    </Link>
  ) : (
    <span className={rowClass} data-testid={`entity-badge-${kind}-${name}`}>{inner}</span>
  );
}
